import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@styled-icons/fa-solid';
import { postTaricDetails, putTaricUpdate } from '../../api/taric';
import './CommodityCodeDetail.css';
import { Button, Alert } from 'react-bootstrap';

const API_CONFIG = {
  import: {
    base: ['commcode', 'VAT'],
    additionalCodes: ['taricAddCode1', 'taricAddCode2'],
    docCodes: Array.from({ length: 8 }, (_, i) => `docCode${i + 1}`),
    descriptions: Array.from({ length: 8 }, (_, i) => `description${i + 1}`),
  },
  export: {
    base: ['expCommodityCode'],
    additionalCodes: ['expTaricAddCode1', 'expTaricAddCode2'],
    docCodes: Array.from({ length: 6 }, (_, i) => `expDocCode${i + 1}`),
    descriptions: Array.from({ length: 6 }, (_, i) => `expDescription${i + 1}`),
  },
};

const generateApiKeys = (commCode) => {
  const keys = {};

  API_CONFIG.import.base.forEach((key) => {
    keys[key] = key === 'commcode' ? commCode : null;
  });
  API_CONFIG.import.additionalCodes.forEach((key) => {
    keys[key] = null;
  });
  API_CONFIG.import.docCodes.forEach((key) => {
    keys[key] = null;
  });
  API_CONFIG.import.descriptions.forEach((key) => {
    keys[key] = null;
  });

  API_CONFIG.export.base.forEach((key) => {
    keys[key] = key === 'expCommodityCode' ? commCode.substring(0, 8) : null;
  });
  API_CONFIG.export.additionalCodes.forEach((key) => {
    keys[key] = null;
  });
  API_CONFIG.export.docCodes.forEach((key) => {
    keys[key] = null;
  });
  API_CONFIG.export.descriptions.forEach((key) => {
    keys[key] = null;
  });

  return keys;
};

const extractDataFromApi = (apiData, commCode, isImport = true) => {
  const config = isImport ? API_CONFIG.import : API_CONFIG.export;
  const defaultKeys = generateApiKeys(commCode);

  if (apiData && apiData.length > 0) {
    const data = apiData[0];
    const result = {};

    config.base.forEach((key) => {
      if (isImport && key === 'commcode') {
        result[key] = data.commodityCode || commCode;
      } else if (!isImport && key === 'expCommodityCode') {
        result[key] = data.expCommodityCode || commCode.substring(0, 8);
      } else {
        result[key] = data[key] || '';
      }
    });

    config.additionalCodes.forEach((key) => {
      result[key] = data[key] || '';
    });

    config.docCodes.forEach((key) => {
      result[key] = data[key] || '';
    });

    config.descriptions.forEach((key) => {
      result[key] = data[key] || '';
    });

    if (isImport && data.vatRates !== undefined) {
      result.VAT = data.vatRates || '';
    }

    return result;
  }

  const result = {};
  [...config.base, ...config.additionalCodes, ...config.docCodes, ...config.descriptions].forEach((key) => {
    result[key] = defaultKeys[key] || '';
  });
  if (isImport) {
    result.VAT = defaultKeys.VAT || '';
  }

  return result;
};

const renderDocumentCodesTable = (data, setData, isImport = true) => {
  const config = isImport ? API_CONFIG.import : API_CONFIG.export;

  return (
    <table className="table table-bordered" style={{ width: '100%', background: '#fff' }}>
      <thead>
        <tr>
          <th>No.</th>
          <th>Code</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {config.docCodes.map((codeKey, index) => {
          const rowNum = index + 1;
          const descKey = config.descriptions[index];
          return (
            <tr key={rowNum}>
              <td>
                <input type="text" className="form-control" value={rowNum} readOnly />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={data[codeKey]}
                  onChange={(e) => setData({ ...data, [codeKey]: e.target.value })}
                />
              </td>
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={data[descKey]}
                  onChange={(e) => setData({ ...data, [descKey]: e.target.value })}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const CommodityCodeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastConfig, setSuccessToastConfig] = useState({
    title: '',
    message: '',
    type: 'success',
  });

  const [importData, setImportData] = useState(() => extractDataFromApi(null, '', true));
  const [exportData, setExportData] = useState(() => extractDataFromApi(null, '', false));

  useEffect(() => {
    loadCommodityData();
  }, [id]);

  const loadCommodityData = async () => {
    try {
      const response = await postTaricDetails(id);

      setImportData(extractDataFromApi(response.importDetails, id, true));
      setExportData(extractDataFromApi(response.exportDetails, id, false));
    } catch (err) {
      console.error('Failed to load commodity code data:', err);
      setSuccessToastConfig({
        title: 'Load Error',
        message: 'Failed to load commodity code data. Please try again.',
        type: 'error',
      });
      setShowSuccessToast(true);
    }
  };

  const handleSave = async () => {
    try {
      const baseKeys = generateApiKeys(importData.commcode);

      const saveData = {
        ...baseKeys,
        ...importData,
        ...exportData,
      };

      const cleanSaveData = Object.fromEntries(
        Object.entries(saveData).filter(([key, value]) => value !== null && value !== '' && value !== undefined)
      );

      const response = await putTaricUpdate(cleanSaveData);

      console.log('Save successful:', response);

      setSuccessToastConfig({
        title: 'Save Success',
        message: 'Taric details have been saved successfully.',
        type: 'success',
      });
      setShowSuccessToast(true);
    } catch (err) {
      setSuccessToastConfig({
        title: 'Save Error',
        message: 'Failed to save changes. Please try again.',
        type: 'error',
      });
      setShowSuccessToast(true);
    }
  };

  const handleCancel = () => {
    navigate('/TaricTool');
  };

  const addYCode = (type) => {
    if (type === 'import') {
      if (importData.yCodes.length < 8) {
        setImportData({
          ...importData,
          yCodes: [...importData.yCodes, ''],
          logicConditions: [...importData.logicConditions, ''],
        });
      }
    } else {
      if (exportData.yCodes.length < 6) {
        setExportData({
          ...exportData,
          yCodes: [...exportData.yCodes, ''],
          logicConditions: [...exportData.logicConditions, ''],
        });
      }
    }
  };

  const removeYCode = (type, index) => {
    if (type === 'import') {
      const newYCodes = importData.yCodes.filter((_, i) => i !== index);
      const newLogic = importData.logicConditions.filter((_, i) => i !== index);
      setImportData({
        ...importData,
        yCodes: newYCodes,
        logicConditions: newLogic,
      });
    } else {
      const newYCodes = exportData.yCodes.filter((_, i) => i !== index);
      const newLogic = exportData.logicConditions.filter((_, i) => i !== index);
      setExportData({
        ...exportData,
        yCodes: newYCodes,
        logicConditions: newLogic,
      });
    }
  };

  return (
    <div className="full-width-container">
      {showSuccessToast && (
        <div className="success-toast-container">
          <Alert
            className={successToastConfig.type === 'error' ? 'error-toast-alert' : 'success-toast-alert'}
            onClose={() => {
              setShowSuccessToast(false);
            }}
            dismissible
          >
            <div className="success-toast-header">
              <span className={successToastConfig.type === 'error' ? 'error-toast-title' : 'success-toast-title'}>
                {successToastConfig.title}
              </span>
            </div>
            <p className={successToastConfig.type === 'error' ? 'error-toast-message' : 'success-toast-message'}>
              {successToastConfig.message}
            </p>
          </Alert>
        </div>
      )}

      <section className="floating-box position-relative" style={{ maxWidth: '1200px' }}>
        <h1 className="floating-box-header-newload">TARIC Tool</h1>
        <div style={{ padding: '16px 32px', marginBottom: '0', minHeight: '200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <Button
              variant="link"
              onClick={() => navigate('/TaricTool')}
              className="back-button"
              style={{ padding: 0, margin: 0, color: '#0d6efd', textDecoration: 'underline' }}
            >
              <ArrowLeft size={16} />
              Back to Taric Tool
            </Button>
          </div>

          <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
            <div
              className="import-section"
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: '500px' }}
            >
              <div className="section-header">
                <h5>IMPORT MEASURES for {importData.commcode} (10)</h5>
              </div>

              <div className="mt-3">
                <h6>Document Codes</h6>
                <div style={{ overflowX: 'auto', marginTop: 24 }}>
                  {renderDocumentCodesTable(importData, setImportData, true)}
                </div>
              </div>

              <div className="mt-2">
                <h6>Taric Additional Code</h6>
                <div style={{ overflowX: 'auto', marginTop: 24 }}>
                  <table className="table table-bordered" style={{ width: '100%', background: '#fff' }}>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={importData.taricAddCode1}
                            onChange={(e) => {
                              setImportData({ ...importData, taricAddCode1: e.target.value });
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={importData.taricAddCode2}
                            onChange={(e) => {
                              setImportData({ ...importData, taricAddCode2: e.target.value });
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div
              className="export-section"
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: '500px' }}
            >
              <div className="section-header">
                <h5>EXPORT MEASURES FOR {exportData.expCommodityCode} (8)</h5>
              </div>

              <div className="mt-3">
                <h6>Documents Codes</h6>
                <div style={{ overflowX: 'auto', marginTop: 24 }}>
                  {renderDocumentCodesTable(exportData, setExportData, false)}
                </div>
              </div>

              <div style={{ flex: 1, minHeight: '100px' }}></div>

              <div>
                <h6>Taric Additional Code</h6>
                <div style={{ overflowX: 'auto', marginTop: 24 }}>
                  <table className="table table-bordered" style={{ width: '100%', background: '#fff' }}>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={exportData.expTaricAddCode1}
                            onChange={(e) => {
                              setExportData({ ...exportData, expTaricAddCode1: e.target.value });
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={exportData.expTaricAddCode2}
                            onChange={(e) => {
                              setExportData({ ...exportData, expTaricAddCode2: e.target.value });
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4">
                <h6>VAT Rates</h6>
                <div style={{ overflowX: 'auto', marginTop: 24 }}>
                  <table className="table table-bordered" style={{ width: '100%', background: '#fff' }}>
                    <tbody>
                      <tr>
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={importData.VAT}
                            onChange={(e) => setImportData({ ...importData, VAT: e.target.value })}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: 20,
              height: 70,
              alignItems: 'center',
            }}
          >
            <button
              type="button"
              className="blue-button-teams green-btn"
              onClick={handleCancel}
              style={{ minWidth: 100, height: 40 }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="blue-button-teams green-btn"
              onClick={() => navigate(`/taric/${id}/history`)}
              style={{ minWidth: 100, height: 40 }}
            >
              History
            </button>
            <button
              type="button"
              className="blue-button-teams"
              onClick={handleSave}
              style={{ minWidth: 100, height: 40 }}
            >
              Save
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommodityCodeDetail;
